import React from "react";

import { CartDrawer, IconSuccess, type CartDrawerProps } from "@bared/ui";
import { Sheet } from "~/components/Sheet";

import {
  useCart,
  useCartToggle,
  useCartIsLoading,
  useCartLineItemRemove,
  useCartToggleAccessory,
} from "~/hooks/useCart";
import { useSite } from "~/hooks/useSite";

export function Cart() {
  const site = useSite();
  const { data: cart } = useCart();

  const { cartIsOpen, closeCart } = useCartToggle();

  const { removeLineItem } = useCartLineItemRemove();
  const { toggleAccessory } = useCartToggleAccessory();

  const isLoading = useCartIsLoading();

  const handleCloseCart: CartDrawerProps["onClose"] = () => {
    closeCart();
  };

  const handleLineRemove: CartDrawerProps["onLineRemove"] = ({
    cartLineId,
  }) => {
    removeLineItem({ cartLineId: cartLineId });
  };

  const handleAccessoryToggle: CartDrawerProps["onAccessoryToggle"] = ({
    enabled,
    cartLineId,
    accessoryType,
  }) => {
    toggleAccessory({ enabled, cartLineId, accessoryType });
  };

  const handleTooltipOpen = () => {
    const drawer = document.getElementById("cart-line-items");

    if (!drawer) return;

    drawer.style.overflowY = "hidden";
  };

  const handleTooltipClose = () => {
    const drawer = document.getElementById("cart-line-items");

    if (!drawer) return;

    drawer.style.removeProperty("overflow-y");
  };

  // Build accessible label for dialog that includes flash message
  const accessibleLabel = cart?.custom.flashMessage?.message
    ? `${cart.custom.flashMessage.message}. Your Cart`
    : "Your Cart";

  return (
    <Sheet
      layout="condensed"
      isOpen={cartIsOpen}
      onClose={handleCloseCart}
      className="flex flex-col"
      eventTitle="Cart"
      ariaLabel={accessibleLabel}
      title={
        cart && cart.custom.flashMessage ? (
          <div className="flex space-x-micro">
            <IconSuccess className="w-mini text-primary" />
            <p className="text-caption leading-caption">
              {cart.custom.flashMessage.message}
            </p>
          </div>
        ) : (
          "Your Cart"
        )
      }
    >
      <CartDrawer
        cart={cart ?? null}
        isLoading={isLoading}
        freeShippingThreshold={site.freeShippingThreshold}
        onClose={handleCloseCart}
        onLineRemove={handleLineRemove}
        className="flex-1 overflow-y-auto"
        onAccessoryToggle={handleAccessoryToggle}
        onTooltipOpen={handleTooltipOpen}
        onTooltipClose={handleTooltipClose}
      />
    </Sheet>
  );
}
